import React, { useEffect, useState } from "react";
import { Star } from "lucide-react";
import api from "../../lib/api";

/**
 * Testimonials — 6 short reviews spanning weddings, birthdays, graduations,
 * corporate events, memorials, and engagements. Rendered as a 3x2 grid on
 * desktop, single column on mobile. Admin-managed via `/api/testimonials` —
 * when the collection is empty, the hardcoded `FALLBACK_REVIEWS` below show
 * so the section never renders blank.
 */
const FALLBACK_REVIEWS = [
  {
    id: "wed-priya",
    stars: 5,
    quote: "Every guest scanned the QR at the ceremony — by the time we sat down for dinner we had 800 photos we'd never have seen. Worth every penny.",
    name: "Priya & Marcus",
    event: "Wedding · London",
    tag: "Weddings",
  },
  {
    id: "bday-elena",
    stars: 5,
    quote: "My mum's 60th birthday. Aunties who barely text sent 40 photos each. She cried the next morning going through the gallery.",
    name: "Elena Rossi",
    event: "60th Birthday · Milan",
    tag: "Birthdays",
  },
  {
    id: "grad-devon",
    stars: 5,
    quote: "Graduation ceremony + family lunch + campus tour — three moments, one gallery. Grandparents in Ghana felt like they were there.",
    name: "Devon O.",
    event: "Graduation · Atlanta",
    tag: "Graduations",
  },
  {
    id: "corp-nadia",
    stars: 5,
    quote: "We collected 1,400 photos across our EMEA leadership summit without printing a single card. Marketing had usable assets by breakfast.",
    name: "Nadia Bekele",
    event: "Corporate Summit · Dubai",
    tag: "Corporate",
  },
  {
    id: "mem-hannah",
    stars: 5,
    quote: "For my grandad's memorial. Family from four countries added photos and messages from decades we'd never seen. It became our keepsake.",
    name: "Hannah Fischer",
    event: "Memorial · Berlin",
    tag: "Memorials",
  },
  {
    id: "eng-adaeze",
    stars: 5,
    quote: "Our engagement in the garden — the surprise, the tears, the aunties in gele. GalerieCop held every angle. Set-up took maybe 4 minutes.",
    name: "Adaeze & Kolawole",
    event: "Engagement · Lagos",
    tag: "Engagements",
  },
];

const Testimonials = () => {
  const [reviews, setReviews] = useState(FALLBACK_REVIEWS);

  useEffect(() => {
    let cancelled = false;
    api.get("/testimonials")
      .then((r) => {
        if (cancelled) return;
        const items = r.data?.items || [];
        if (items.length > 0) setReviews(items);
      })
      .catch(() => { /* fall back to hardcoded */ });
    return () => { cancelled = true; };
  }, []);

  if (!reviews.length) return null;
  const [featured, ...rest] = reviews;

  return (
    <section
      className="section bg-white"
      data-testid="testimonials-section"
      aria-labelledby="testimonials-heading"
    >
      <div className="max-w-[1400px] mx-auto px-5 lg:px-10">
        <div className="text-center max-w-2xl mx-auto">
          <p className="tracking-widest text-coral-300 text-xs font-semibold uppercase">
            Loved by hosts, worldwide
          </p>
          <h2
            id="testimonials-heading"
            className="mt-4 font-serif text-navy-700 text-4xl md:text-5xl leading-tight"
          >
            A little something from <span className="italic">every kind of gathering</span>
          </h2>
        </div>

        {/* Featured — large centered hero quote. Coral-tinted, spotlight card. */}
        <figure
          data-testid={`testimonial-${featured.id}`}
          className="mt-12 mx-auto max-w-3xl bg-coral-100/60 border-2 border-coral-300 rounded-3xl p-8 md:p-12 text-center soft-shadow-coral relative"
        >
          <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-coral-300 text-white text-[11px] font-bold tracking-wider uppercase px-4 py-1.5 rounded-full">
            Featured story · {featured.tag}
          </span>
          <div className="flex justify-center gap-1" aria-label={`${featured.stars} out of 5 stars`}>
            {Array.from({ length: featured.stars }).map((_, i) => (
              <Star key={i} className="w-5 h-5 text-coral-300 fill-coral-300" />
            ))}
          </div>
          <blockquote className="mt-6 font-serif italic text-navy-700 text-2xl md:text-3xl leading-relaxed">
            &ldquo;{featured.quote}&rdquo;
          </blockquote>
          <figcaption className="mt-6 text-base">
            <div className="text-navy-700 font-semibold">{featured.name}</div>
            <div className="text-navy-700/60">{featured.event}</div>
          </figcaption>
        </figure>

        {/* The other 5 reviews — smaller grid below the featured one */}
        <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {rest.map((r) => (
            <figure
              key={r.id}
              data-testid={`testimonial-${r.id}`}
              className="bg-cream-100/70 rounded-2xl p-6 md:p-7 flex flex-col soft-shadow"
            >
              <div className="flex items-center justify-between">
                <div className="flex" aria-label={`${r.stars} out of 5 stars`}>
                  {Array.from({ length: r.stars }).map((_, i) => (
                    <Star key={i} className="w-4 h-4 text-coral-300 fill-coral-300" />
                  ))}
                </div>
                <span className="text-[10px] uppercase tracking-wider text-navy-700/50 font-semibold bg-white rounded-full px-2 py-1">
                  {r.tag}
                </span>
              </div>
              <blockquote className="mt-4 font-serif italic text-navy-700 text-lg leading-relaxed flex-1">
                &ldquo;{r.quote}&rdquo;
              </blockquote>
              <figcaption className="mt-5 text-sm">
                <div className="text-navy-700 font-semibold">{r.name}</div>
                <div className="text-navy-700/60">{r.event}</div>
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
